import { lazy } from 'react';

// ── lazily loaded pages ──────────────────────────────────────────────────────
// Each deploy replaces every hashed chunk, so a tab opened before a deploy
// fails to fetch the next page it navigates to (Netlify's SPA fallback answers
// the missing .js with index.html). Reloading once picks up the new
// index.html and its chunk names. The flag in sessionStorage marks that one
// reload, so a chunk that is genuinely broken fails instead of reloading
// forever.

export const CHUNK_RELOAD_FLAG = 'dzose-chunk-reload';

// Storage can throw (blocked site data, some private modes). Each helper
// reports whether it worked rather than letting that escape.
const hasFlag = () => {
  try {
    return window.sessionStorage.getItem(CHUNK_RELOAD_FLAG) !== null;
  } catch (e) {
    return false;
  }
};

const setFlag = () => {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
    return true;
  } catch (e) {
    return false;
  }
};

const clearFlag = () => {
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
  } catch (e) {
    // Nothing to clear if storage is unavailable.
  }
};

export const loadWithReload = (load, reload = () => window.location.reload()) =>
  load().then(
    module => {
      clearFlag();
      return module;
    },
    error => {
      // Already reloaded once for this, or cannot record that we are about
      // to: surface the error rather than risk a reload loop.
      if (hasFlag() || !setFlag()) {
        clearFlag();
        throw error;
      }
      reload();
      // Never settles: the page is going away.
      return new Promise(() => {});
    }
  );

// React.lazy for a route's page component. `load` is `() => import('...')`.
export const lazyPage = load => lazy(() => loadWithReload(load));
